
import {Observable, Subject} from 'rxjs';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';

import Parse from 'parse/dist/parse.min.js';
import {FirebaseOperationChat, FirebaseUser} from "@app/models/FirebaseModels";
import {ChatMessage} from "@app/models/chat";
import {HttpClient} from "@angular/common/http";
import {Utils} from "../../../utils";
import {Assistance} from "../../../models/assistance";

export interface IFileMetaData {
  meta: any;
  url: string;
}

@Injectable()
export class ParseChatService {

  private initialized: boolean;
  public token: string;

  constructor() {
    this.init();
  }

  init() {

    // Check parse connection
    if (environment.authProvider == 'parse' && !this.initialized) {
      Parse.initialize(environment.parse.appId, environment.parse.javascriptKey);
      Parse.serverURL = environment.parse.serverURL;
      Parse.liveQueryServerURL = environment.parse.liveQueryServerURL;
      this.initialized = true;
    }

  }

  get ParseChat() {
    return Parse.Object.extend("Chat");
  }

  get ParseChatMessage() {
    return Parse.Object.extend("Message");
  }

  get ParseTillyUser() {
    return Parse.Object.extend("TillyUser");
  }

  async chatExists(chatId: string) {
    const query = new Parse.Query(this.ParseChat);
    query.equalTo('chatId', `ticket_${chatId}`);
    query.equalTo('roomId', '100');
    return (await query.find()).length > 0;
  }

  async getChat(chatId: string) {
    let chat;
    while (!chat) {
      const query = new Parse.Query(this.ParseChat);
      query.equalTo('chatId', chatId);
      query.equalTo('roomId', '100');
      chat = (await query.find())[0];
      await Utils.sleep(1000);
    }
    return chat;
  }

  async createChat(chat: FirebaseOperationChat) {
    const _chat = new this.ParseChat();
    _chat.set('chatCreatedAt', Date.now());
    _chat.set('chatId', `ticket_${chat.id}`);
    _chat.set('roomId', '100');
    _chat.save();
  }

  async joinChat(chatId: string, user: FirebaseUser) {

    // Create TillyUser if not exists
    const userQuery = new Parse.Query(this.ParseTillyUser);
    userQuery.equalTo('userID', user.userID);
    const users = await userQuery.find();
    const userExists = users.length > 0;
    const newUser = new this.ParseTillyUser();
    if (!userExists) {
      newUser.set('firstName', user.firstName);
      newUser.set('lastName', user.lastName);
      newUser.set('userID', user.userID);
      newUser.save();
    }
    const _user = users[0] || newUser;

    // Add user to chat
    const chat = await this.getChat(chatId);
    chat.relation('users').add(_user);
    chat.save();

  }

  async getChatMessages(chatId: string, pageSize: number): Promise<Observable<any>> {

    // Subscribe with parse live query
    const chat = await this.getChat(chatId);
    const query = new Parse.Query(chat);
    const subscription = await query.subscribe();

    // Send messages as soon as chat changes
    const messages$ = new Subject();
    subscription.on('open', async () => {
      messages$.next((await chat.relation('messages').query().descending('sentAt').find()).reverse());
    });
    subscription.on('update', async (chat) => {
      messages$.next((await chat.relation('messages').query().descending('sentAt').find()).reverse());
      // messages$.next((await message.relation('messages').query().descending('sentAt').limit(pageSize).find()).reverse());
    });

    return messages$.asObservable();
  }

  async sendMessage(chatId: string, message: ChatMessage) {

    const chat = await this.getChat(chatId);

    // Create message
    const _message = new this.ParseChatMessage();
    _message.set('media', message.media);
    _message.set('card', message.card);
    _message.set('fromID', message.fromID);
    _message.set('message', message.message || '');
    _message.set('readAt', message.readAt || {});
    _message.set('sentAt', Date.now());
    _message.set('deliveredAt', message.deliveredAt || {});
    _message.set('service', false);
    _message.set('deleted', false);
    _message.set('type', message.type);
    _message.set('options', []);
    await _message.save();

    // Update messageID for backward compatibility
    /*_message.set('messageID', _message.id);
    _message.save();*/

    // Add message to chat
    chat.relation('messages').add(_message);
    chat.save();

  }

  // TODO to check with Parse
  sendVideocallRequest(assistance: Assistance, operatorId: string, pin: string) {
    this.sendMessage(`ticket_${assistance.id}`, {
      fromID: operatorId,
      type: 100,
      card: {
        button: {
          text: 'CONTINUA',
          type: 'videoCall'
        },
        info: [{ label: 'Inquadra il QR per iniziare' }],
        leftIcon: {
          type: 'new_assistance'
        },
        qrCode: `${pin};ev:${assistance.id}`,
        title: 'Richiesta videochiamata',
        type: 'videoCall'
      },
    } as ChatMessage)
  }

}
